import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Card } from '@/shared/components/ui/card'
import { buildLeadFilterUrl } from '@/shared/lib/leadNavigation'

import SalespersonLeadsModal from './SalespersonLeadsModal'

export default function ConversionCard({ data, period, customRange }) {
  const navigate = useNavigate()
  const [selectedPerson, setSelectedPerson] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const content =
    data && !Array.isArray(data) && typeof data === 'object'
      ? data
      : {
          total: 0,
          wonCount: 0,
          lostCount: 0,
          totalLeads: 0,
          percentage: 0,
          subtitle: 'converted · by salesperson',
          salespersons: [],
        }

  const salespersons = content.salespersons ?? []
  const overallPct = content.percentage ?? 0
  const wonCount = content.wonCount ?? content.total ?? 0
  const lostCount = content.lostCount ?? 0
  const totalLeads = content.totalLeads ?? 0

  const handlePersonClick = (person) => {
    const salesPersonId = person.salesId || person._id
    if (salesPersonId) {
      setSelectedPerson({
        salesId: salesPersonId,
        name: person.name,
        achievement: person.wonCount ?? person.converted ?? 0,
        orders: person.wonCount ?? person.converted ?? 0,
        revenue: person.revenue ?? 0,
        target: person.target ?? 0,
      })
      setIsModalOpen(true)
    } else {
      // Fallback: navigate to leads
      navigate(buildLeadFilterUrl('/admin/leads', { period, customRange }))
    }
  }

  return (
    <>
      <Card className="h-[260px] overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)] sm:rounded-[20px]">
        <div className="flex h-full flex-col p-4">
          {/* Static Header */}
          <div className="shrink-0">
            <h3 className="text-sm font-semibold text-slate-900">
              Conversion Rate
            </h3>

            <div className="mt-2 flex flex-wrap items-end gap-2">
              <span className="text-[clamp(1.75rem,4vw,2.25rem)] font-extrabold leading-none tracking-tight text-slate-900">
                {overallPct}%
              </span>

              <span className="pb-1 text-xs text-slate-500">overall rate</span>
            </div>

            <p className="mt-1 text-[11px] text-slate-400">
              {wonCount} won · {lostCount} lost of {totalLeads} leads
            </p>
          </div>

          {/* Scrollable Salespersons */}
          <div className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
            {salespersons.length === 0 ? (
              <p className="py-4 text-center text-xs text-slate-400">
                No data yet
              </p>
            ) : (
              <div className="space-y-2">
                {salespersons.map((person, index) => (
                  <div
                    key={`${person.name}-${index}`}
                    className="cursor-pointer rounded-lg px-1 py-0.5 transition hover:bg-slate-50"
                    onClick={() => handlePersonClick(person)}
                    title={`View leads for ${person.name}`}
                  >
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-medium text-slate-700 hover:text-violet-700">
                        {person.name}
                      </span>

                      <span className="shrink-0 text-[10px]">
                        <span className="font-semibold text-emerald-600">
                          {person.wonCount ?? person.converted ?? 0}W
                        </span>
                        <span className="text-slate-300"> / </span>
                        <span className="font-semibold text-red-500">
                          {person.lostCount ?? 0}L
                        </span>
                        <span className="text-slate-400">
                          {' '}
                          ({person.total})
                        </span>

                        <span className="font-medium text-violet-600">
                          {' '}
                          · {person.percentage}%
                        </span>
                      </span>
                    </div>

                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
                        style={{
                          width: `${Math.min(
                            100,
                            Number(person.percentage) || 0,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      <SalespersonLeadsModal
        person={selectedPerson}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        period={period}
        customRange={customRange}
      />
    </>
  )
}
